import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthStore } from '@stores/authStore';
import {
  getStoreManagerFeed,
  STORE_MANAGER_READ_EVENT,
} from '@lib/services/storeManager/storeManagerFeedApi';

/**
 * How many Store Manager messages the merchant has not seen (#5.5).
 *
 * <p><b>One hook, so the two dots cannot disagree.</b> The sidebar entry and the Home Workspace card
 * both show this. Fetching it in each place would give two numbers from two moments — and a sidebar
 * dot that stays after the card's has gone reads as a bug in the feature that is supposed to tell the
 * merchant what is new.
 *
 * <p><b>Re-reads on navigation.</b> Opening the Store Manager marks everything read server-side, so the
 * count is stale the instant they leave that page. Keying the fetch on the pathname refreshes it when
 * they navigate away, which is the moment the dot has to disappear. Cheap: the request asks for one
 * message and reads only the count.
 *
 * <p>Failure is 0, deliberately: a dot that cannot be justified should not be drawn. Claiming "you have
 * something new" and then showing an empty feed is worse than missing a real one for a few seconds.
 */
export function useStoreManagerUnread(): number {
  const userId = useAuthStore(s => s.user?.id);
  const { pathname } = useLocation();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!userId) {
      setUnread(0);
      return;
    }
    let cancelled = false;
    getStoreManagerFeed(userId, { limit: 1 })
      .then(feed => {
        if (!cancelled) setUnread(feed.unreadCount ?? 0);
      })
      .catch(() => {
        if (!cancelled) setUnread(0);
      });
    return () => {
      cancelled = true;
    };
  }, [userId, pathname]);

  // The Store Manager page marks the feed read the moment it opens, but this hook does not poll and
  // its fetch is already keyed on the current route — so nothing re-read the count while the merchant
  // sat on the page, and the dot stayed lit until they navigated away. Listen for the read event and
  // drop it in place. Optimistic 0 is safe: the event fires only after the read POST resolved.
  useEffect(() => {
    const onRead = () => setUnread(0);
    window.addEventListener(STORE_MANAGER_READ_EVENT, onRead);
    return () => window.removeEventListener(STORE_MANAGER_READ_EVENT, onRead);
  }, []);

  return unread;
}
